import { isNativeApp } from "@/lib/native"

// =====================================================
// BOOTH — TikTok Pixel helpers
// =====================================================
// Thin, typed wrapper around the global `ttq` installed by
// <TikTokPixel> (components/analytics/tiktok-pixel.tsx). Deliberately
// the same shape as lib/meta-pixel.ts — the two pixels are wired at the
// same call sites, so keeping the APIs symmetrical means a new
// conversion is one line per network, not a new mental model.
//
// Every helper is a no-op when the pixel isn't loaded — no env var
// configured, running on the server, or a tracker blocker ate the
// script — so call sites never need to guard.
//
// Only STANDARD events are used. TikTok Events Manager only offers
// events the pixel has actually received when you pick an ad group's
// optimization event, so the names below are what shows up there once
// real traffic fires them. Keep this list in sync with docs/TIKTOK-PIXEL.md.
// =====================================================

/**
 * Standard TikTok events BOOTH fires. Restricted on purpose.
 *
 * TikTok's vocabulary differs from Meta's — the equivalents we use:
 *   Meta Lead            → SubmitForm
 *   Meta Purchase        → CompletePayment
 *   Meta PageView        → ttq.page() (not a `track` event)
 */
export type TikTokStandardEvent =
  // Account created — the primary optimization event for the owner/barber ads.
  | "CompleteRegistration"
  // Inquiry sent on a hiring post / booth listing — mid-funnel intent.
  | "SubmitForm"
  // Viewed a specific listing or hiring post.
  | "ViewContent"
  // Started a paid flow (reservation / subscription checkout).
  | "InitiateCheckout"
  // Paid.
  | "CompletePayment"

type TtqParams = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    ttq?: {
      page: (...args: unknown[]) => void
      track: (...args: unknown[]) => void
      identify: (...args: unknown[]) => void
      instance: (...args: unknown[]) => unknown
      load: (...args: unknown[]) => void
    }
    TiktokAnalyticsObject?: string
  }
}

export const TIKTOK_PIXEL_ID = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID

/**
 * True when the pixel is configured and the script has loaded in this tab.
 *
 * Always false inside the native shell — same reasoning as the Meta pixel:
 * the layout keeps the script out of the app, and this stops a call added
 * later from quietly reintroducing tracking there.
 */
export function tiktokPixelReady(): boolean {
  if (isNativeApp()) return false
  return typeof window !== "undefined" && typeof window.ttq?.track === "function"
}

/**
 * Fire a standard TikTok event.
 *
 * `params` is passed through to TikTok — never put PII in it (no email,
 * phone, or name). TikTok treats those as Advanced Matching fields, which
 * we deliberately don't send.
 */
export function trackTikTok(event: TikTokStandardEvent, params?: TtqParams): void {
  if (!tiktokPixelReady()) return
  try {
    window.ttq!.track(event, params)
  } catch {
    // Analytics must never break a user flow.
  }
}

/**
 * Pageview. TikTok models this as `ttq.page()` rather than a tracked event
 * name, so it gets its own helper instead of going through trackTikTok.
 */
export function trackTikTokPageView(): void {
  if (!tiktokPixelReady()) return
  try {
    window.ttq!.page()
  } catch {
    // Analytics must never break a user flow.
  }
}
